// ============================================================================
// checklistDetailVersionService — versioned checklist detail content for the
// Checklist Books.
// ----------------------------------------------------------------------------
// Mirrors the existing layoutVersionService, but for recurring definition
// fields (description, assignee, proof rules, ratings, etc.). Title remains
// global on the definition doc, and live occurrence state stays on pages.
//
// Pages can also carry a small `detailPatch` map used for "Only this
// instance" edits without minting a full future branch.
// ============================================================================

import {
  collection,
  deleteField,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  serverTimestamp,
  where,
  writeBatch,
} from 'firebase/firestore'
import { firestoreDb } from './firebase'
import {
  type BookId,
  type ChecklistBook,
  type ChecklistBookPage,
  type ChecklistDefinition,
  type ChecklistDefinitionVersionPatch,
  type ChecklistDetailVersion,
  type ChecklistType,
  type DetailResolutionResult,
  type DetailVersionId,
  type PageKey,
  bookPath,
  detailVersionPath,
  DETAIL_VERSIONS_SUBCOL,
  PAGES_SUBCOL,
  pagePath,
} from '../types/checklistBooks'
import {
  primeChecklistDetailVersionCache,
  primeChecklistPageCache,
  readCachedDetailVersion,
} from './checklistReadCache'

export const CHECKLIST_DETAIL_VERSIONED_FIELDS = [
  'description',
  'completionCriteria',
  'priority',
  'weightage',
  'isDescriptionPrivate',
  'countdownHours',
  'assigneeId',
  'assigneeName',
  'assigneeHistoryIds',
  'attachedFormIds',
  'attachedDesignIds',
  'cardBackgroundFileId',
  'cardBackgroundUrl',
  'cardBackgroundFileName',
  'cardBackgroundStoragePath',
  'prerequisites',
  'dependencyNodeIds',
  'invalidDependencyNodeIds',
  'blockedByExternal',
  'blockedByExternalId',
  'interWorkspaceTargetId',
  'dependencyLogicGate',
  'blockedByNodeIds',
  'isBlockerForNodeIds',
  'nodeKind',
  'gateInputIds',
  'hasAutoSpawnTrigger',
  'autoSpawnTemplates',
  'requiresProofFile',
  'requiresCoSign',
  'coSignerRoleTarget',
  'proofApproverId',
  'proofApproverName',
  'requiresLocationPin',
  'targetCoordinates',
  'enforcePaintSpringDelay',
  'delayDurationHours',
  'estimatedHours',
  'complexityStars',
  'urgencyStars',
  'importanceStars',
  'currencyCode',
  'helperVisibilityMask',
  'isHelperVisible',
  'readAccess',
  'writeAccess',
  'requiredStockIds',
  'stockRequirements',
  'consumptionMode',
] as const satisfies ReadonlyArray<keyof ChecklistDefinitionVersionPatch>

export const CHECKLIST_RECURRING_GLOBAL_FIELDS = ['title'] as const

export const CHECKLIST_RECURRING_SCHEDULE_FIELDS = [
  'scheduleMode',
  'scheduleStartDate',
  'scheduleEndDate',
  'hasTimeWindow',
  'scheduleTimeFrom',
  'scheduleTimeTo',
  'startDate',
  'endDate',
] as const

const VERSIONED_FIELD_SET = new Set<string>(CHECKLIST_DETAIL_VERSIONED_FIELDS)
const RECURRING_GLOBAL_FIELD_SET = new Set<string>(CHECKLIST_RECURRING_GLOBAL_FIELDS)
const RECURRING_SCHEDULE_FIELD_SET = new Set<string>(CHECKLIST_RECURRING_SCHEDULE_FIELDS)

const bookRef = (bookId: BookId) => doc(firestoreDb, bookPath(bookId))
const pageRef = (bookId: BookId, pageKey: PageKey) => doc(firestoreDb, pagePath(bookId, pageKey))
const detailRef = (bookId: BookId, versionId: DetailVersionId) => doc(firestoreDb, detailVersionPath(bookId, versionId))
const pagesCollectionRef = (bookId: BookId) => collection(firestoreDb, `${bookPath(bookId)}/${PAGES_SUBCOL}`)
const detailVersionsCollectionRef = (bookId: BookId) => collection(firestoreDb, `${bookPath(bookId)}/${DETAIL_VERSIONS_SUBCOL}`)

export type DetailVersionActor = {
  uid: string
  displayName?: string | null
}

const clonePatch = (
  patch: ChecklistDefinitionVersionPatch | null | undefined,
): ChecklistDefinitionVersionPatch => {
  if (!patch) return {}
  return JSON.parse(JSON.stringify(patch)) as ChecklistDefinitionVersionPatch
}

const normalizePatch = (
  patch: ChecklistDefinitionVersionPatch,
): ChecklistDefinitionVersionPatch => {
  const next: Record<string, unknown> = {}
  for (const key of CHECKLIST_DETAIL_VERSIONED_FIELDS) {
    if (!(key in patch)) continue
    const value = patch[key]
    if (value === undefined) continue
    next[key] = value
  }
  return next as ChecklistDefinitionVersionPatch
}

const mergePatches = (
  base: ChecklistDefinitionVersionPatch | null | undefined,
  patch: ChecklistDefinitionVersionPatch | null | undefined,
): ChecklistDefinitionVersionPatch => ({
  ...clonePatch(base),
  ...normalizePatch(clonePatch(patch)),
})

export const isChecklistDetailVersionedField = (key: string): boolean =>
  VERSIONED_FIELD_SET.has(key)

export const isChecklistRecurringGlobalField = (key: string): boolean =>
  RECURRING_GLOBAL_FIELD_SET.has(key)

export const isChecklistRecurringScheduleField = (key: string): boolean =>
  RECURRING_SCHEDULE_FIELD_SET.has(key)

export const hasChecklistDetailPatch = (
  patch: Record<string, ChecklistDefinitionVersionPatch> | null | undefined,
): boolean =>
  Boolean(
    patch
    && Object.values(patch).some((entry) => Object.keys(entry ?? {}).length > 0),
  )

export const pickChecklistDetailVersionPatch = (
  updates: Record<string, unknown>,
): ChecklistDefinitionVersionPatch => {
  const patch: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(updates)) {
    if (!VERSIONED_FIELD_SET.has(key)) continue
    if (value === undefined) continue
    patch[key] = value
  }
  return patch as ChecklistDefinitionVersionPatch
}

export const applyChecklistDetailPatch = <T extends ChecklistDefinition>(
  definition: T,
  versionPatch?: ChecklistDefinitionVersionPatch | null,
  pagePatch?: ChecklistDefinitionVersionPatch | null,
): T => ({
  ...definition,
  ...clonePatch(versionPatch),
  ...clonePatch(pagePatch),
}) as T

export const getDetailVersion = async (
  bookId: BookId,
  versionId: DetailVersionId,
): Promise<ChecklistDetailVersion | null> => {
  return readCachedDetailVersion(bookId, versionId, async () => {
    const snap = await getDoc(detailRef(bookId, versionId))
    return snap.exists() ? (snap.data() as ChecklistDetailVersion) : null
  })
}

export const resolveInheritedDetailVersion = async (
  bookId: BookId,
  pageKey: PageKey,
): Promise<DetailResolutionResult> => {
  const priorQuery = query(
    pagesCollectionRef(bookId),
    where('pageKey', '<', pageKey),
    orderBy('pageKey', 'desc'),
    limit(1),
  )
  const priorSnap = await getDocs(priorQuery)
  if (!priorSnap.empty) {
    const priorPage = priorSnap.docs[0].data() as ChecklistBookPage
    if (priorPage.detailVersionId) {
      return {
        kind: 'inherited',
        versionId: priorPage.detailVersionId,
        sourcePageKey: priorPage.pageKey,
      }
    }
  }

  const bookSnap = await getDoc(bookRef(bookId))
  const book = bookSnap.exists() ? (bookSnap.data() as ChecklistBook) : null
  if (book?.activeDetailVersionId) {
    return { kind: 'active', versionId: book.activeDetailVersionId }
  }
  return { kind: 'none' }
}

export const createDetailVersion = async (
  bookId: BookId,
  workspaceId: string,
  checklistPatches: Record<string, ChecklistDefinitionVersionPatch>,
  actor: DetailVersionActor,
  opts: { parentVersionId?: DetailVersionId | null } = {},
): Promise<DetailVersionId> => {
  const newRef = doc(detailVersionsCollectionRef(bookId))
  const version: ChecklistDetailVersion = {
    id: newRef.id,
    bookId,
    workspaceId,
    checklistPatches: Object.fromEntries(
      Object.entries(checklistPatches)
        .map(([id, patch]) => [id, normalizePatch(clonePatch(patch))] as const)
        .filter(([, patch]) => Object.keys(patch).length > 0),
    ),
    parentVersionId: opts.parentVersionId ?? null,
    createdAt: serverTimestamp(),
    createdById: actor.uid,
    createdByName: actor.displayName ?? null,
    recordVersion: 1,
  }
  const batch = writeBatch(firestoreDb)
  batch.set(newRef, version)
  await batch.commit()
  primeChecklistDetailVersionCache(bookId, newRef.id, version)
  return newRef.id
}

export const ensureActiveDetailVersion = async (
  bookId: BookId,
  workspaceId: string,
  actor: DetailVersionActor,
): Promise<DetailVersionId> => {
  const snap = await getDoc(bookRef(bookId))
  if (snap.exists()) {
    const book = snap.data() as ChecklistBook
    if (book.activeDetailVersionId) return book.activeDetailVersionId
  }
  const versionId = await createDetailVersion(bookId, workspaceId, {}, actor)
  const batch = writeBatch(firestoreDb)
  const type: ChecklistType = bookId.endsWith('_DAILY') ? 'DAILY'
    : bookId.endsWith('_WEEKLY') ? 'WEEKLY'
    : 'INDIVIDUAL'
  const bookPayload: Record<string, unknown> = {
    activeDetailVersionId: versionId,
    updatedAt: serverTimestamp(),
  }
  if (!snap.exists()) {
    bookPayload.id = bookId
    bookPayload.workspaceId = workspaceId
    bookPayload.checklistType = type
    bookPayload.createdAt = serverTimestamp()
    bookPayload.recordVersion = 1
  }
  batch.set(bookRef(bookId), bookPayload, { merge: true })
  await batch.commit()
  return versionId
}

export const setChecklistDetailPatchOnPage = async (
  bookId: BookId,
  pageKey: PageKey,
  checklistId: string,
  patch: ChecklistDefinitionVersionPatch,
  actor: DetailVersionActor,
): Promise<void> => {
  const snap = await getDoc(pageRef(bookId, pageKey))
  if (!snap.exists()) throw new Error(`setChecklistDetailPatchOnPage: page ${pageKey} is missing in book ${bookId}`)
  const page = snap.data() as ChecklistBookPage
  const nextEntry = mergePatches(page.detailPatch?.[checklistId], patch)
  const nextPatch = {
    ...(page.detailPatch ?? {}),
    [checklistId]: nextEntry,
  }
  const batch = writeBatch(firestoreDb)
  batch.update(pageRef(bookId, pageKey), {
    detailPatch: nextPatch,
    updatedAt: serverTimestamp(),
    lastUpdatedBy: actor.uid,
    lastUpdatedByName: actor.displayName ?? null,
    recordVersion: (page.recordVersion ?? 0) + 1,
  })
  await batch.commit()
  primeChecklistPageCache(bookId, pageKey, {
    ...page,
    detailPatch: nextPatch,
    lastUpdatedBy: actor.uid,
    lastUpdatedByName: actor.displayName ?? null,
    recordVersion: (page.recordVersion ?? 0) + 1,
  })
}

const clearChecklistDetailPatchEntry = (
  detailPatch: Record<string, ChecklistDefinitionVersionPatch> | null | undefined,
  checklistId: string,
): Record<string, ChecklistDefinitionVersionPatch> => {
  const next = { ...(detailPatch ?? {}) }
  delete next[checklistId]
  return next
}

// Compute today's page key in the format matching the anchor — DAILY keys
// (YYYY-MM-DD) sort lexicographically in chronological order, so does the
// ISO week format (YYYY-Www) within a year, but DAILY keys and WEEKLY keys
// don't share an ordering (`W` sorts after `0-9`), so we must match the
// anchor's shape before comparing. Duplicated from checklistBookService to
// avoid a circular import (checklistBookService already imports from this
// module).
const todayPageKeyMatching = (anchorKey: string): string | null => {
  const now = new Date()
  if (/^\d{4}-\d{2}-\d{2}$/.test(anchorKey)) {
    const y = now.getFullYear()
    const m = String(now.getMonth() + 1).padStart(2, '0')
    const d = String(now.getDate()).padStart(2, '0')
    return `${y}-${m}-${d}`
  }
  if (/^\d{4}-W\d{2}$/.test(anchorKey)) {
    // ISO-week key aligned to the Mon-anchored week for the current date.
    const t = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()))
    const dayOfWeek = t.getUTCDay() || 7
    t.setUTCDate(t.getUTCDate() + 4 - dayOfWeek)
    const yearStart = new Date(Date.UTC(t.getUTCFullYear(), 0, 1))
    const weekNumber = Math.ceil((((t.getTime() - yearStart.getTime()) / 86_400_000) + 1) / 7)
    return `${t.getUTCFullYear()}-W${String(weekNumber).padStart(2, '0')}`
  }
  return null
}

export const applyDetailEditToPage = async (
  bookId: BookId,
  workspaceId: string,
  anchorPageKey: PageKey,
  checklistId: string,
  patch: ChecklistDefinitionVersionPatch,
  actor: DetailVersionActor,
): Promise<{ versionId: DetailVersionId; repointedPageKeys: PageKey[] }> => {
  const anchorSnap = await getDoc(pageRef(bookId, anchorPageKey))
  if (!anchorSnap.exists()) {
    throw new Error(`applyDetailEditToPage: anchor page ${anchorPageKey} does not exist in book ${bookId}`)
  }
  const anchorPage = anchorSnap.data() as ChecklistBookPage

  // Anchor MUST already carry a detailVersionId. Prior behavior fell through
  // to `ensureActiveDetailVersion(...)` here and the forward walk repointed
  // every subsequent uninitialised page onto the freshly-minted (possibly
  // empty) version — asymmetric with `layoutVersionService.applyEditToPage`
  // (line 266-269), which hard-fails on the same condition. `ensurePage` in
  // `checklistBookService` is the sole authority for initialising a page's
  // detailVersionId; both callers of this function (`checklistMutationService`
  // and `useChecklistDetailController.saveDraft`) call
  // `ensurePageDetailVersionForKey(...)` before this. If the anchor arrived
  // without one, either that bootstrap step was skipped or the page is
  // corrupted — fail loud instead of silently repointing.
  if (typeof anchorPage.detailVersionId !== 'string' || anchorPage.detailVersionId.length === 0) {
    throw new Error(
      `applyDetailEditToPage: anchor page ${anchorPageKey} in book ${bookId} has no detailVersionId — call ensurePageDetailVersionForKey(...) first`,
    )
  }
  const oldVersionId = anchorPage.detailVersionId

  // Past-anchor observability. Edits anchored on a page BEFORE today still
  // propagate forward and repoint every currently-shared page onto the new
  // version — that IS the intended semantic for "This and future" saves
  // triggered from a past occurrence. We log a warn so ops has a signal
  // when this fires, since the same code path is easy to invoke by
  // accident (user viewing a past occurrence, edits detail, hits Save)
  // and the ripple can touch many pages. Behavior is unchanged.
  const todayKey = todayPageKeyMatching(anchorPageKey)
  if (todayKey && anchorPageKey < todayKey) {
    console.warn('[applyDetailEditToPage] anchor page is before today; repointing propagates from past forward', {
      bookId, anchorPageKey, checklistId, today: todayKey,
    })
  }
  const baseVersion = oldVersionId ? await getDetailVersion(bookId, oldVersionId) : null
  const nextChecklistPatches = {
    ...(baseVersion?.checklistPatches ?? {}),
    [checklistId]: mergePatches(
      mergePatches(baseVersion?.checklistPatches?.[checklistId], anchorPage.detailPatch?.[checklistId]),
      patch,
    ),
  }

  const forwardQuery = query(
    pagesCollectionRef(bookId),
    where('pageKey', '>=', anchorPageKey),
    orderBy('pageKey', 'asc'),
  )
  const forwardSnap = await getDocs(forwardQuery)

  // Propagation stops at the first forward page whose detail-version has
  // already diverged, OR whose `detailPatch` carries a "single-instance"
  // override for THIS SAME checklistId — foreign checklists' overrides on
  // later pages must not truncate this checklist's future reach. Prior
  // behavior used `hasChecklistDetailPatch(page.detailPatch)` which halted
  // on any override for any checklist and silently shortened propagation.
  const toRepoint: PageKey[] = []
  const repointedPages: ChecklistBookPage[] = []
  for (const docSnap of forwardSnap.docs) {
    const page = docSnap.data() as ChecklistBookPage
    const pageVersionId = page.detailVersionId ?? oldVersionId
    if (pageVersionId !== oldVersionId) break
    if (page.pageKey !== anchorPageKey) {
      const ownPatch = page.detailPatch?.[checklistId]
      if (ownPatch && Object.keys(ownPatch).length > 0) break
    }
    toRepoint.push(page.pageKey)
    repointedPages.push(page)
  }

  const newRef = doc(detailVersionsCollectionRef(bookId))
  const newVersion: ChecklistDetailVersion = {
    id: newRef.id,
    bookId,
    workspaceId,
    checklistPatches: nextChecklistPatches,
    parentVersionId: oldVersionId,
    createdAt: serverTimestamp(),
    createdById: actor.uid,
    createdByName: actor.displayName ?? null,
    recordVersion: 1,
  }

  const batch = writeBatch(firestoreDb)
  batch.set(newRef, newVersion)
  for (const key of toRepoint) {
    const updates: Record<string, unknown> = {
      detailVersionId: newRef.id,
      updatedAt: serverTimestamp(),
      lastUpdatedBy: actor.uid,
      lastUpdatedByName: actor.displayName ?? null,
    }
    if (key === anchorPageKey) {
      const anchorPatch = clearChecklistDetailPatchEntry(anchorPage.detailPatch, checklistId)
      updates.detailPatch = Object.keys(anchorPatch).length > 0 ? anchorPatch : deleteField()
    }
    batch.update(pageRef(bookId, key), updates)
  }
  batch.set(bookRef(bookId), {
    activeDetailVersionId: newRef.id,
    updatedAt: serverTimestamp(),
  }, { merge: true })
  await batch.commit()
  primeChecklistDetailVersionCache(bookId, newRef.id, newVersion)
  repointedPages.forEach((page) => {
    const isAnchorPage = page.pageKey === anchorPageKey
    const anchorPatch = isAnchorPage
      ? clearChecklistDetailPatchEntry(anchorPage.detailPatch, checklistId)
      : page.detailPatch
    primeChecklistPageCache(bookId, page.pageKey, {
      ...page,
      detailVersionId: newRef.id,
      detailPatch: anchorPatch,
      lastUpdatedBy: actor.uid,
      lastUpdatedByName: actor.displayName ?? null,
    })
  })
  return { versionId: newRef.id, repointedPageKeys: toRepoint }
}
